import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { dataClient } from '../../data/client';
import type { AggregatesArtifact, EncodedResponse, JapanMapArtifact, KansaiMapArtifact, ResponsesArtifact } from '../../data/types';
import { ParticleField } from '../../motion/particles/ParticleField';
import { useMotionPreferences } from '../../motion/preferences';
import { scrollBehaviorFor, useSharedScrollRuntime } from '../../motion/scrollRuntime';
import { useAppMode } from '../../state/appMode';
import { PresenterProgress } from '../../state/PresenterProgress';
import { ReportStateProvider, useReportState } from '../../state/reportState';
import { OnboardingShell } from '../onboarding/OnboardingShell';
import { matchingRespondentIds, type OnboardingAnswers } from '../onboarding/cohort';
import { Dashboard } from '../dashboard/Dashboard';
import { VoicesExperience } from '../voices/VoicesExperience';
import { SectionRail } from '../shell/SectionRail';
import { DataStoryInterludes } from './DataStoryInterludes';
import { REPORT_ESSAYS, ReportEssayInterlude } from './ReportEssayInterlude';
import { StoryChapter } from './StoryChapter';
import { StoryScrollProgress } from './StoryScrollProgress';
import { MethodologySection } from './MethodologySection';
import { isReportDestinationHash, parseStoryHash, STORY_CHAPTERS, type StoryBeat, type StoryChapterNumber, type StoryLocation } from './types';

interface StoryExperienceProps { aggregates: AggregatesArtifact; responses: ResponsesArtifact | null; introComplete: boolean; }
const EMPTY_ANSWERS: OnboardingAnswers = { visit: null, age: null };

function StoryExperienceBody({ aggregates, responses, introComplete }: StoryExperienceProps) {
  const { mode, registerChapterNavigation } = useAppMode();
  const { section, setSection, setTab, setFilters, setMapLocation, filters } = useReportState();
  const deepLinkedChapter = useMemo(() => parseStoryHash(window.location.hash), []);
  const [onboardingComplete, setOnboardingComplete] = useState(() => isReportDestinationHash(window.location.hash));
  const [answers, setAnswers] = useState<OnboardingAnswers>(EMPTY_ANSWERS);
  const [location, setLocation] = useState<StoryLocation>({ chapter: deepLinkedChapter ?? 1, beat: 0 });
  const [map, setMap] = useState<KansaiMapArtifact | null>(null);
  const [japanMap, setJapanMap] = useState<JapanMapArtifact | null>(null);
  const [mapError, setMapError] = useState(false);
  const [inDataStories, setInDataStories] = useState(false);
  const [selectedPrefecture, setSelectedPrefecture] = useState<string | undefined>();
  const headingRefs = useRef(new Map<number, HTMLHeadingElement>());
  const observerLockUntil = useRef(0);
  const presenter = mode === 'presenter';
  const { reducedMotion } = useMotionPreferences();
  useSharedScrollRuntime(onboardingComplete && !presenter);
  const activeDefinition = STORY_CHAPTERS[location.chapter - 1]!;
  const activeBeat = activeDefinition.beats[location.beat]!;
  const particleConfig = onboardingComplete ? activeBeat : { layout: 'field' as const };
  const matchedIds = useMemo(() => responses && !onboardingComplete ? matchingRespondentIds(responses, answers) : undefined, [answers, onboardingComplete, responses]);

  const moveTo = useCallback((next: StoryLocation, focus = true) => {
    observerLockUntil.current = Date.now() + 900;
    setLocation(next);
    history.replaceState(null, '', `#chapter-${next.chapter}`);
    requestAnimationFrame(() => {
      if (!presenter) document.querySelector<HTMLElement>(`[data-story-beat="${next.chapter}:${next.beat}"]`)?.scrollIntoView({ block: 'center', behavior: scrollBehaviorFor(reducedMotion) });
      if (focus) headingRefs.current.get(next.chapter)?.focus({ preventScroll: true });
    });
  }, [presenter, reducedMotion]);

  const previous = useCallback(() => {
    if (location.beat > 0) moveTo({ ...location, beat: (location.beat - 1) as StoryBeat });
    else if (location.chapter > 1) moveTo({ chapter: (location.chapter - 1) as StoryChapterNumber, beat: 2 });
  }, [location, moveTo]);
  const next = useCallback(() => {
    if (location.beat < 2) moveTo({ ...location, beat: (location.beat + 1) as StoryBeat });
    else if (location.chapter < 6) moveTo({ chapter: (location.chapter + 1) as StoryChapterNumber, beat: 0 });
  }, [location, moveTo]);

  useEffect(() => {
    if (!onboardingComplete) return;
    let cancelled = false;
    Promise.all([dataClient.loadKansaiMap(), dataClient.loadJapanMap()])
      .then(([kansai, japan]) => { if (!cancelled) { setMap(kansai); setJapanMap(japan); } })
      .catch(() => { if (!cancelled) setMapError(true); });
    return () => { cancelled = true; };
  }, [onboardingComplete]);

  useEffect(() => {
    if (!onboardingComplete || !introComplete || !deepLinkedChapter) return;
    requestAnimationFrame(() => {
      observerLockUntil.current = Date.now() + 700;
      if (!presenter) {
        const root = document.documentElement;
        const previousBehavior = root.style.scrollBehavior;
        root.style.scrollBehavior = 'auto';
        document.querySelector<HTMLElement>(`[data-story-beat="${deepLinkedChapter}:0"]`)?.scrollIntoView({ block: 'center' });
        root.style.scrollBehavior = previousBehavior;
      }
      setLocation({ chapter: deepLinkedChapter, beat: 0 });
      history.replaceState(null, '', `#chapter-${deepLinkedChapter}`);
      headingRefs.current.get(deepLinkedChapter)?.focus({ preventScroll: true });
    });
  }, [deepLinkedChapter, introComplete, onboardingComplete, presenter]);

  useEffect(() => {
    if (!onboardingComplete || !introComplete || section === 'story') return;
    requestAnimationFrame(() => document.getElementById(section)?.scrollIntoView({ block: 'start', behavior: scrollBehaviorFor(reducedMotion) }));
  }, [introComplete, onboardingComplete, reducedMotion, section]);

  useEffect(() => {
    if (!onboardingComplete) return;
    return registerChapterNavigation({ previous: location.chapter > 1 || location.beat > 0 ? previous : undefined, next: location.chapter < 6 || location.beat < 2 ? next : undefined });
  }, [location, next, onboardingComplete, previous, registerChapterNavigation]);

  useEffect(() => {
    if (presenter || !onboardingComplete || typeof IntersectionObserver === 'undefined') return;
    const visibleBeats = new Map<Element, number>();
    const observer = new IntersectionObserver((entries) => {
      if (Date.now() < observerLockUntil.current) return;
      for (const entry of entries) {
        if (entry.isIntersecting) visibleBeats.set(entry.target, entry.intersectionRatio);
        else visibleBeats.delete(entry.target);
      }
      const readingLine = window.innerHeight * 0.48;
      const visible = [...visibleBeats.keys()].sort((a, b) => {
        const aRect = a.getBoundingClientRect();
        const bRect = b.getBoundingClientRect();
        return Math.abs(aRect.top + aRect.height / 2 - readingLine) - Math.abs(bRect.top + bRect.height / 2 - readingLine);
      })[0];
      const token = visible?.getAttribute('data-story-beat');
      if (!token) return;
      const [chapterText, beatText] = token.split(':');
      const chapter = Number(chapterText) as StoryChapterNumber;
      const beat = Number(beatText) as StoryBeat;
      setLocation((current) => current.chapter === chapter && current.beat === beat ? current : { chapter, beat });
      history.replaceState(null, '', `#chapter-${chapter}`);
    }, { rootMargin: '-34% 0px -38%', threshold: [0, 0.15, 0.5] });
    document.querySelectorAll<HTMLElement>('[data-story-beat]').forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [onboardingComplete, presenter]);

  useEffect(() => {
    if (presenter || !onboardingComplete || typeof IntersectionObserver === 'undefined') return;
    const sectionElement = document.querySelector<HTMLElement>('[data-data-stories]');
    if (!sectionElement) return;
    const observer = new IntersectionObserver(([entry]) => setInDataStories(Boolean(entry?.isIntersecting)), {
      rootMargin: '-5% 0px -5%',
      threshold: 0.01,
    });
    observer.observe(sectionElement);
    return () => observer.disconnect();
  }, [onboardingComplete, presenter]);

  const completeOnboarding = useCallback(() => {
    setOnboardingComplete(true);
    history.replaceState(null, '', '#chapter-1');
    requestAnimationFrame(() => headingRefs.current.get(1)?.focus({ preventScroll: presenter }));
  }, [presenter]);
  const selectPrefecture = useCallback((prefecture: string) => {
    setSelectedPrefecture(prefecture); setMapLocation(prefecture);
    setFilters({ ...filters, area: { ...filters.area, prefectures: [prefecture] } });
  }, [filters, setFilters, setMapLocation]);
  const handoff = useCallback((tab: Parameters<typeof setTab>[0], destination: 'dashboard' | 'voices' = 'dashboard') => { setTab(tab); setSection(destination); }, [setSection, setTab]);

  return <div className="relative min-h-svh bg-atc-base text-atc-ink">
    <div className="atc-flow-field pointer-events-none fixed inset-0 z-0" aria-hidden="true" />
    {responses && <ParticleField rows={responses.rows} layout={particleConfig.layout} bandField={'bandField' in particleConfig ? particleConfig.bandField as keyof EncodedResponse : undefined} matchedIds={matchedIds} radiusScale={presenter ? 1.35 : 1} className="fixed inset-0 z-0 opacity-[.2]" />}
    {!onboardingComplete ? <OnboardingShell aggregates={aggregates} responses={responses} answers={answers} onAnswersChange={setAnswers} onComplete={completeOnboarding} /> : <>
      <SectionRail />
      <main className="relative z-10">
        <PresenterProgress current={location.chapter} total={6} label={`${activeDefinition.marker} · ${location.beat + 1}/3`} />
        {!presenter && <StoryScrollProgress location={location} railHidden={inDataStories} onNavigate={(nextLocation) => moveTo(nextLocation)} />}
        {STORY_CHAPTERS.map((definition) => {
          const essay = REPORT_ESSAYS.find((item) => item.afterChapter === definition.number);
          return <Fragment key={definition.id}>
            <StoryChapter ref={(node) => { if (node) headingRefs.current.set(definition.number, node); else headingRefs.current.delete(definition.number); }} definition={definition} aggregates={aggregates} presenter={presenter} active={definition.number === location.chapter} activeBeat={location.beat} map={map} japanMap={japanMap} mapError={mapError} selectedPrefecture={selectedPrefecture} onSelectPrefecture={selectPrefecture} onHandoff={handoff} />
            {!presenter && essay && <ReportEssayInterlude definition={essay} aggregates={aggregates} onHandoff={handoff} />}
          </Fragment>;
        })}
        {!presenter && <DataStoryInterludes aggregates={aggregates} onHandoff={(tab) => handoff(tab)} />}
        <nav className={presenter ? 'fixed bottom-6 right-6 z-40 flex gap-3' : `${inDataStories ? 'invisible pointer-events-none' : ''} sticky bottom-5 z-40 mx-auto mb-8 flex w-fit gap-3 rounded-full border border-atc-navy/10 bg-white/90 p-2 shadow-atc-card backdrop-blur transition-opacity`} aria-label="ストーリーの移動">
          <button type="button" disabled={location.chapter === 1 && location.beat === 0} onClick={previous} className="rounded-full border border-atc-navy/15 bg-white px-5 py-3 text-sm font-bold disabled:opacity-30">前へ</button>
          <span className="grid place-items-center px-2 text-sm font-bold tabular-nums">{location.chapter}/6 · {location.beat + 1}/3</span>
          <button type="button" disabled={location.chapter === 6 && location.beat === 2} onClick={next} className="rounded-full bg-atc-navy px-5 py-3 text-sm font-bold text-white disabled:opacity-30">次へ</button>
        </nav>
        <Dashboard aggregates={aggregates} />
        <VoicesExperience />
        <MethodologySection />
      </main>
    </>}
  </div>;
}

export function StoryExperience(props: StoryExperienceProps) {
  return <ReportStateProvider responses={props.responses}><StoryExperienceBody {...props} /></ReportStateProvider>;
}
